import { Container } from "../Container/Container";
import { CharityArr } from "./../charity/CharityObj";
import { useLocation } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css/pagination";

import {
  PageOuter,
  PageWrapper,
  BackLink,
  HeroBlock,
  Badge,
  Title,
  Accent,
  SliderBlock,
  SliderImg,
  HeroImageWrap,
  HeroImage,
  ContentCard,
  LeadParagraph,
  Paragraph,
  BulletList,
  BulletItem,
  VideoSection,
  VideoSectionTitle,
  GridContainer,
  NotFoundBlock,
} from "./styled";

import { routes } from "../../shared/routes/routes";

type CharityKey = keyof typeof CharityArr;

type Block =
  | { kind: "para"; text: string; html: boolean }
  | { kind: "bullets"; items: string[] };

const isBullet = (s: string) => /^[-—•]\s+/.test(s.trim());
const stripBullet = (s: string) => s.trim().replace(/^[-—•]\s+/, "");
const hasHtml = (s: string) => /<[a-z][^>]*>/i.test(s);

const groupBlocks = (lines: string[]): Block[] => {
  const blocks: Block[] = [];
  let bulletBuf: string[] = [];

  const flushBullets = () => {
    if (bulletBuf.length) {
      blocks.push({ kind: "bullets", items: bulletBuf });
      bulletBuf = [];
    }
  };

  for (const line of lines) {
    if (isBullet(line)) {
      bulletBuf.push(stripBullet(line));
    } else {
      flushBullets();
      blocks.push({ kind: "para", text: line, html: hasHtml(line) });
    }
  }
  flushBullets();
  return blocks;
};

const getYouTubeVideoId = (url: string) => {
  const match = url.match(
    /(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/|watch\?.+&v=))([^?&]+)/
  );
  return match ? match[1] : null;
};

const renderParaContent = (text: string, html: boolean) => {
  if (html) {
    const safe = text.replace(
      /<a /gi,
      '<a target="_blank" rel="noreferrer" '
    );
    return <span dangerouslySetInnerHTML={{ __html: safe }} />;
  }
  return text;
};

export const CharityPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const location = useLocation();
  const key = location.pathname.split("/")[2] as CharityKey;
  const charityObject = CharityArr[key];

  const blocks = useMemo(
    () => (charityObject ? groupBlocks(charityObject.info) : []),
    [charityObject]
  );

  if (!charityObject) {
    return (
      <Container>
        <PageOuter>
          <BackLink to={routes.charity}>
            <svg viewBox="0 0 14 10" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M13 5H1M1 5L5 1M1 5L5 9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            Все вечера
          </BackLink>
          <NotFoundBlock>Раздел не найден.</NotFoundBlock>
        </PageOuter>
      </Container>
    );
  }

  const hasGallery = charityObject.photos.length > 0;
  const hasSingleImage = !hasGallery && !!charityObject.photosv;
  const hasVideos = charityObject.videos.length > 0;

  const [leadBlock, ...restBlocks] = blocks;
  const leadIsPara = leadBlock && leadBlock.kind === "para";

  return (
    <Container>
      <PageOuter>
        <PageWrapper>
          <BackLink to={routes.charity}>
            <svg viewBox="0 0 14 10" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M13 5H1M1 5L5 1M1 5L5 9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            Все вечера
          </BackLink>

          <HeroBlock>
            <Badge>Благотворительный вечер</Badge>
            <Title>{charityObject.title}</Title>
            <Accent />
          </HeroBlock>

          {hasGallery && (
            <SliderBlock>
              <Swiper
                pagination={{ clickable: true }}
                modules={[Pagination, Autoplay]}
                autoplay={{ delay: 6000, disableOnInteraction: false }}
                loop
              >
                {charityObject.photos.map((photo, index) => (
                  <SwiperSlide key={index}>
                    <SliderImg src={photo} alt={charityObject.title} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </SliderBlock>
          )}

          {hasSingleImage && (
            <HeroImageWrap>
              <HeroImage
                src={charityObject.photosv as string}
                alt={charityObject.title}
              />
            </HeroImageWrap>
          )}

          {blocks.length > 0 && (
            <ContentCard>
              {leadIsPara && (
                <LeadParagraph>
                  {renderParaContent(leadBlock.text, leadBlock.html)}
                </LeadParagraph>
              )}
              {(leadIsPara ? restBlocks : blocks).map((block, idx) => {
                if (block.kind === "bullets") {
                  return (
                    <BulletList key={idx}>
                      {block.items.map((item, i) => (
                        <BulletItem key={i}>{item}</BulletItem>
                      ))}
                    </BulletList>
                  );
                }
                return (
                  <Paragraph key={idx}>
                    {renderParaContent(block.text, block.html)}
                  </Paragraph>
                );
              })}
            </ContentCard>
          )}

          {hasVideos && (
            <VideoSection>
              <VideoSectionTitle>Видео</VideoSectionTitle>
              <GridContainer>
                {charityObject.videos.map((url, index) => {
                  const videoId = getYouTubeVideoId(url);
                  if (!videoId) return null;
                  return (
                    <iframe
                      key={index}
                      src={`https://www.youtube.com/embed/${videoId}`}
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={`YouTube Video ${index + 1}`}
                    />
                  );
                })}
              </GridContainer>
            </VideoSection>
          )}
        </PageWrapper>
      </PageOuter>
    </Container>
  );
};
